import React from 'react';
import {
    connect,
} from 'react-redux';
import PropTypes from 'prop-types';
import {
    AutoSizer,
} from 'react-virtualized';
import {
    Icon,
} from '@iconify/react';
import {
    Image,
    Nav,
    Tab,
    Row,
    Button,
} from 'react-bootstrap';
import SplitPane from 'react-split-pane';
import RoomScene from '@shared/components/RoomScene';
import RoomSwitcher from "@shared/components/RoomSwitcher";
import closeIcon from '@iconify/icons-mdi/close';
import loopIcon from '@iconify/icons-mdi/loop';
import fullScreenOpenIcon from '@iconify/icons-mdi/fullscreen';
import fullScreenCloseIcon from '@iconify/icons-mdi/fullscreen-exit';
import infoIcon from '@iconify/icons-mdi/information-outline';
import imageMultiple from '@iconify/icons-mdi/image-multiple';
import floorPlan from '@iconify/icons-mdi/floor-plan';
import actions from '@shared/actions';
import {
    tourSelector,
} from "@shared/selector";
import FloorTabs from './FloorTabs';
import InfoTab from './InfoTab';
import StandardDialog from "../dialogs/StandardDialog";
import {
    language,
} from '@shared/language';

const i18n = {
    de: {
        dialog_access_title: 'Passwortgeschützter Rundgang',
        dialog_access_message: 'Bitte geben Sie das Passwort zum entsperren der Ansicht an',
        dialog_access_password_placeholder: 'Passwort',
        dialog_access_accept_label: 'Entsperren',

        document_title_fallback: '360°-Rundgang',

        tab_info_title: 'Kontakt',
        tab_rooms_title: 'Vorschaubilder',
        tab_floorplan_title: 'Grundriss',
    },
    en: {
        dialog_access_title: 'Password-protected tour',
        dialog_access_message: 'Please enter the password to unlock the view',
        dialog_access_password_placeholder: 'Password',
        dialog_access_accept_label: 'Unlock',

        document_title_fallback: '360° tour',

        tab_info_title: 'Contact',
        tab_rooms_title: 'Preview images',
        tab_floorplan_title: 'Floorplan',
    },
};

class Viewer extends RoomSwitcher {
    static propTypes = {
        tour: PropTypes.any,
        toggleFullScreen: PropTypes.func,
        fullScreen: PropTypes.bool,
        onShowStandardDialog: PropTypes.func,
        onCloseStandardDialog: PropTypes.func,
    }

    state = {
        autoPlay: false,
        tabKey: null,
        showLogin: false,
        showGreeter: false,
        visitCardClass: '',
        sidePaneClass: '',
        hoverItem: false,
        windowWidth: 0,
    }

    enclosing = React.createRef();

    componentDidMount() {
        this.props.load();
        window.addEventListener('resize', this.onWindowResize);
        this.onWindowResize();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.onWindowResize);
    }

    componentDidUpdate(prevProps) {
        if (this.props.tour && this.props.tour !== prevProps.tour) {
            if (this.props.tour.get('login') && !this.state.showLogin) {
                this.props.onShowStandardDialog({
                    title: i18n[language].dialog_access_title,
                    message: i18n[language].dialog_access_message,
                    enterPassword: {placeholder: i18n[language].dialog_access_password_placeholder, value: ''},
                    noHide: true,
                    noCloseButton: false,
                    noCancelButton: true,
                    accept: {
                        createAction: ({password}) => { this.setState({showLogin: false}); this.props.load(password) },
                        variant: 'primary',
                        label: i18n[language].dialog_access_accept_label,
                    },
                });
                this.setState({showLogin: true});
            } else if (!this.props.tour.get('login') && this.state.showLogin && !this.state.showGreeter) {
                this.props.onCloseStandardDialog();
                this.setState({showLogin: false});
            }
            if (this.props.tour.get('showGreeter') && !this.state.showGreeter) {
                this.props.onShowStandardDialog({
                    title: this.props.tour.get('name'),
                    noCloseButton: false,
                    noFooter: true,
                    greeting: true,
                });
                this.setState({showGreeter: true});
            }
            document.title = this.props.tour.get('name') || i18n[language].docuemnt_title_fallback;
        }
    }

    onWindowResize = () => {
        this.setState({
            windowWidth: window.innerWidth,
            sidePaneClass: window.innerWidth < 500 ? 'small-pane' : '',
            visitCardClass: window.innerWidth < 500 ? 'small-card' : '',
        });
    }

    onTabChange = tabKey => this.setState({tabKey})

    closeTab = () => this.setState({tabKey: null})

    toggleAutoPlay = () => this.setState({autoPlay: !this.state.autoPlay})

    onShowInfo = ({title, text}) => {
        this.props.onShowStandardDialog({
            title: title,
            message: text,
            noFooter: true,
        });
    }

    panes = [
        {
            key: 'info',
            title: i18n[language].tab_info_title,
            icon: infoIcon,
             // eslint-disable-next-line react/display-name
            content: (tour) => <InfoTab tour={tour}
                visitCardClass={this.state.visitCardClass}
                sidePaneClass={this.state.sidePaneClass}
                />,
        },
        {
            key: 'rooms',
            title: i18n[language].tab_rooms_title,
            icon: imageMultiple,
            // eslint-disable-next-line react/display-name
            content: (tour, room) => <FloorTabs
                tour={tour}
                room={room}
                sidePaneClass={this.state.sidePaneClass}
                paneType="rooms"
                onRoomChange={this.onSelectRoom}
                onShowInfo={this.onShowInfo}
            />,
        },
        {
            key: 'floorplan',
            title: i18n[language].tab_floorplan_title,
            icon: floorPlan,
            // eslint-disable-next-line react/display-name
            content: (tour, room) => <FloorTabs
                tour={tour}
                room={room}
                paneType="floorplan"
                onRoomChange={this.onSelectRoom}
                onShowInfo={this.onShowInfo}
            />,
        },
    ];

    render() {
        const {
            tour,
            toggleFullScreen,
            fullScreen,
        } = this.props;

        const {
            tabKey,
            autoPlay,
            sidePaneClass,
            windowWidth,
        } = this.state;

        const {room} = this.getCurrentFloorAndRoom();
        const logoPath = tour && tour.get('showLogo') && tour.get('logo') ? tour.get('logo') : false;
        const primaryColor = tour && tour.get('primaryColor') ? tour.get('primaryColor') : '#009245';
        const secondaryColor = tour && tour.get('secondaryColor') ? tour.get('secondaryColor') : '#ffffff';
        const navPillClass = this.state.tabKey !== null ? 'open' : 'closed';

        return (
            <div
                ref={this.enclosing}
                className="tour-viewport embed-viewport"
            >
                {tour && !tour.get('login') &&
                <Tab.Container
                    id="nav-toolbar"
                    className="nav-toolbar h-100 w-100"
                    activeKey={tabKey}
                    onSelect={this.onTabChange}
                >
                    <SplitPane
                        className={`${sidePaneClass}`}
                        split="vertical"
                        allowResize={tabKey !== null}
                        minSize={tabKey === null ? 0 : 250}
                        size={tabKey === null ? 0 : windowWidth > 625 ? '625px' : '100%'}
                        style={{
                            width: '100%',
                            height: '100%',
                            position: 'relative',
                        }}
                    >
                        <div>
                            <Tab.Content
                                style={{
                                    position: 'absolute',
                                    left: '80px',
                                    top: 0,
                                    bottom: 0,
                                    right: 0,
                                }}
                            >
                                {this.panes.map(pane =>
                                    <Tab.Pane key={pane.key} eventKey={pane.key} className="h-100">
                                        <div
                                            className={[
                                                'd-flex',
                                                'justify-content-between',
                                                'align-items-center',
                                                'border-bottom',
                                                'pl-3',
                                                'background-white',
                                            ].join(' ')}
                                        >
                                            <h1>{pane.title}</h1>
                                            <Button variant="link" onClick={this.closeTab}>
                                                <Icon icon={closeIcon} width="2.0em" />
                                            </Button>
                                        </div>
                                        {pane.content(tour, room)}
                                    </Tab.Pane>,
                                )}
                            </Tab.Content>
                        </div>
                        <div
                            className="w-100 h-100 overflow-hidden"
                        >
                            {room && (
                                <AutoSizer className="px-2">
                                    {({width, height}) => (
                                        <>
                                            <RoomScene
                                                autoPlay={autoPlay}
                                                entity={room}
                                                width={width}
                                                height={height}
                                                readOnly={true}
                                                bottom={tour.get('showBottom')
                                                    ? tour.get('bottom')
                                                    : '/app/images/avatar/avatar_placeholder.jpg'}
                                                onSelectRoom={this.onSelectRoom}
                                                onShowInfo={this.onShowInfo}
                                                primaryColor={primaryColor}
                                                secondaryColor={secondaryColor}
                                            />
                                            {logoPath &&
                                            <Image
                                                src={logoPath}
                                                style={{
                                                    pointerEvents: 'none',
                                                    position: 'absolute',
                                                    width: Math.round(width / 10) + 'px',
                                                    top: 0,
                                                    right: 0,
                                                    margin: 20,
                                                }}
                                            />
                                            }
                                            <Row
                                                className="view-controls"
                                                style={{
                                                    position: 'absolute',
                                                    bottom: 0,
                                                    right: 0,
                                                    margin: 20,
                                                }}
                                            >
                                                <label
                                                    onClick={this.toggleAutoPlay}
                                                >
                                                    <Icon
                                                        style={{color: primaryColor}}
                                                        icon={loopIcon}
                                                        width="2.8em"
                                                    />
                                                </label>
                                                <label
                                                    onClick={toggleFullScreen}
                                                >
                                                    <Icon
                                                        style={{color: primaryColor}}
                                                        icon={fullScreen ? fullScreenCloseIcon : fullScreenOpenIcon}
                                                        width="3.0em"
                                                    />
                                                </label>
                                            </Row>
                                        </>
                                    )}
                                </AutoSizer>
                            )}
                        </div>
                    </SplitPane>
                    <Nav
                        variant="pills"
                        className={`flex-column mt-2 ${navPillClass}`}
                        style={{
                            position: 'fixed',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: '80px',
                        }}
                    >
                        {this.panes.map(pane => {
                            if (pane.key === 'floorplan') {
                                const hasFloorplans = tour.get('floors').filter(floor => floor.get('floorplan') !== '');
                                if (hasFloorplans.toJSON().length === 0) {
                                    return null;
                                }
                            }
                            return <Nav.Item key={pane.key}>
                                <Nav.Link
                                    eventKey={pane.key}
                                    onClick={this.handlePaneClass}
                                >
                                    <Icon
                                        style={{color: primaryColor}}
                                        icon={pane.icon}
                                        width="3em" />
                                </Nav.Link>
                            </Nav.Item>;
                        })}
                    </Nav>
                </Tab.Container>
                }
                <StandardDialog
                    tour={tour}
                    container={this.enclosing}
                />
            </div>
        );
    }
}

const mapStateToProps = state => ({tour: tourSelector(state)});

const mapDispatchToProps = {
    load: actions.data.load,
    onShowStandardDialog: actions.dialog.standard.start,
    onCloseStandardDialog: actions.dialog.standard.close,
};

export default connect(mapStateToProps, mapDispatchToProps)(Viewer);
